/**
 * How long ago, in the shortest form that is still unambiguous: `12s`, `15m`,
 * `8h`, `3d`, `5mo`, `2y`.
 *
 * Not `Intl.RelativeTimeFormat`, which writes "3 days ago" — a phrase as wide as
 * the column it sits in and read no more precisely for it. In a table the
 * figure is scanned, not read, and the unit is one character of shape.
 *
 * The units are Latin in every locale on purpose: they are notation, like `KB`
 * or `ms`, and a reader who has learnt the column once should not have to learn
 * it again in another language.
 */

/** Seconds in each step, and what to call it. */
const STEPS: [seconds: number, unit: string][] = [
  [1, 's'],
  [60, 'm'],
  [60 * 60, 'h'],
  [24 * 60 * 60, 'd'],
  // A month is thirty days and a year is 365. Nobody counts calendar months at
  // this resolution, and pretending to would be a figure that changes when the
  // clock crosses a month boundary rather than when time passes.
  [30 * 24 * 60 * 60, 'mo'],
  [365 * 24 * 60 * 60, 'y'],
];

/**
 * The gap between two instants, as `<number><unit>`.
 *
 * Under a second reads as `0s` rather than as nothing: a row written a moment
 * ago and a row with no time at all must not look alike.
 */
export function shortGap(fromMs: number, toMs: number): string {
  const seconds = Math.abs(toMs - fromMs) / 1000;
  let chosen = STEPS[0];
  for (const step of STEPS) {
    if (seconds >= step[0]) chosen = step;
  }
  const [size, unit] = chosen;
  return `${String(Math.floor(seconds / size))}${unit}`;
}
